//
// Lists, generates and revokes an API user's tokens (admins only).
//
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { fetchApiTokens, generateApiToken, revokeApiToken } from '@actions/auth-actions';
import {
  Alert,
  Box,
  Button,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui';
import Dialog from '@shared/dialogs/dialog';
import { formatDate } from '@utils/shared-utils';

const TOKEN_FIELD_ID = 'api-tokens-dialog-new-token';

// Hidden visually, but read by screen readers.
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1
};

const NewToken = ({ token, onDone }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
    } catch {
      // Clipboard access denied, the user can still select and copy the field.
      document.getElementById(TOKEN_FIELD_ID)?.select();
    }
  }, [token]);

  return (
    <Box mb={3}>
      <Alert severity="warning">
        Copy the &quot;{token.name}&quot; token now and send it to the API client securely.
        You won&apos;t be able to see it again.
      </Alert>
      <Box display="flex" alignItems="center" mt={2}>
        <TextField
          id={TOKEN_FIELD_ID}
          label="API token"
          fullWidth
          value={token.token}
          InputProps={{ readOnly: true }}
          onFocus={event => event.target.select()}
        />
        <Button color="primary" onClick={onCopy} style={{ marginLeft: '1rem' }}>COPY</Button>
        <Button onClick={onDone} style={{ marginLeft: '0.5rem' }}>DONE</Button>
      </Box>
      <div role="status" aria-live="polite">
        {copied && <Typography variant="body2">Copied to the clipboard.</Typography>}
      </div>
    </Box>
  );
};

NewToken.propTypes = {
  onDone: PropTypes.func,
  token: PropTypes.object
};

const ApiTokensDialog = ({ onClose, userId }) => {
  const dispatch = useDispatch();
  const [tokens, setTokens] = useState(null);
  const [name, setName] = useState('');
  const [newToken, setNewToken] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [pending, setPending] = useState(false);

  const loadTokens = useCallback(async () => {
    const data = await dispatch(fetchApiTokens(userId));
    setTokens(data || []);
  }, [dispatch, userId]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const onGenerate = useCallback(async event => {
    event.preventDefault();
    setPending(true);
    const data = await dispatch(generateApiToken(userId, name.trim()));
    setPending(false);
    if (data) {
      setNewToken(data);
      setName('');
      loadTokens();
    }
  }, [dispatch, loadTokens, name, userId]);

  const onRevoke = useCallback(async tokenId => {
    setPending(true);
    await dispatch(revokeApiToken(userId, tokenId));
    setPending(false);
    setConfirmRevokeId(null);
    loadTokens();
  }, [dispatch, loadTokens, userId]);

  return (
    <Dialog title="API tokens" maxWidth="md" onClose={onClose}>
      {newToken && <NewToken token={newToken} onDone={() => setNewToken(null)} />}
      <form onSubmit={onGenerate}>
        <Box display="flex" alignItems="center" mb={3}>
          <TextField
            label="New token name"
            helperText="Identifies the API client using the token (e.g. the integration name)."
            fullWidth
            value={name}
            onChange={event => setName(event.target.value)}
            disabled={pending}
          />
          <Button
            type="submit"
            color="primary"
            disabled={pending || !name.trim()}
            style={{ marginLeft: '1rem', whiteSpace: 'nowrap' }}
          >
            GENERATE TOKEN
          </Button>
        </Box>
      </form>
      {tokens === null && <Progress />}
      {tokens?.length === 0 && <Typography>This user has no active API tokens.</Typography>}
      {tokens?.length > 0 && (
        <Table size="small" aria-label="Active API tokens">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Token</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell><span style={visuallyHidden}>Actions</span></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map(token => (
              <TableRow key={token.id}>
                <TableCell>{token.name}</TableCell>
                <TableCell>{token.token_key}…</TableCell>
                <TableCell>{formatDate(token.created)}</TableCell>
                <TableCell>{token.created_by}</TableCell>
                <TableCell>{token.expiry ? formatDate(token.expiry) : 'Never'}</TableCell>
                <TableCell align="right">
                  {confirmRevokeId === token.id ? (
                    <Button
                      color="secondary"
                      disabled={pending}
                      onClick={() => onRevoke(token.id)}
                      aria-label={`Confirm revoking the ${token.name} token`}
                    >
                      CONFIRM REVOKE
                    </Button>
                  ) : (
                    <Button
                      disabled={pending}
                      onClick={() => setConfirmRevokeId(token.id)}
                      aria-label={`Revoke the ${token.name} token`}
                    >
                      REVOKE
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Dialog>
  );
};

ApiTokensDialog.propTypes = {
  onClose: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default ApiTokensDialog;
